import { useMemo } from "react";
import { CalendarDays, History, Info, LockKeyhole, Video } from "lucide-react";
import Title from "../components/ui/Title";
import WebinaireList from "../components/webinaires/WebinaireList";
import { Skeleton } from "../components/ui/skeleton";
import { Alert } from "../components/ui/alert";
import { usePeutAccederAuxWebinaires, useWebinaires } from "../lib/hooks/useWebinaires";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Webinaire } from "../lib/interfaces/webinaire.interface";

export default function WebinairesPage() {
  const { data: allowed, isError: allowedError } = usePeutAccederAuxWebinaires();
  const { data: webinaires, isError: webinairesError } = useWebinaires({
    enabled: allowed === true,
    staleTime: 1000 * 60 * 5,
  });

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const { futurs, passes } = useMemo(() => {
    if (!webinaires) return { futurs: [], passes: [] };
    const futurs: Webinaire[] = [];
    const passes: Webinaire[] = [];
    for (const w of webinaires) {
      const date = new Date(w.date);
      if (date >= today) {
        futurs.push(w);
      } else {
        passes.push(w);
      }
    }
    futurs.sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());
    passes.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
    return { futurs, passes };
  }, [webinaires, today]);

  return (
    <div className="mx-auto w-full max-w-7xl pb-8">
      <div className="mb-7">
        <Title>Mes webinaires</Title>
        <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600 md:text-base">
          Participez aux prochaines rencontres et retrouvez les replays disponibles.
        </p>
      </div>

      {(allowedError || webinairesError) && (
        <Alert variant="destructive" className="mb-6">
          Impossible de charger les webinaires. Réessayez dans quelques instants.
        </Alert>
      )}

      {!allowedError && allowed === true && !webinairesError ? (
        <>
          <div className="mb-5 flex items-start gap-3 rounded-xl border border-[#ead893] bg-[#fff9e8] px-4 py-3 text-sm leading-5 text-[#725716]">
            <Info className="mt-0.5 h-4 w-4 shrink-0" />
            <p>Programme provisoire, susceptible d'être modifié.</p>
          </div>

          <Tabs defaultValue="futurs">
            <TabsList className="h-11 w-full justify-start rounded-xl bg-slate-100 p-1 sm:w-auto">
              <TabsTrigger
                value="futurs"
                className="flex-1 rounded-lg px-4 data-[state=active]:text-slate-900 sm:flex-none"
              >
                À venir ({futurs.length})
              </TabsTrigger>
              <TabsTrigger
                value="passes"
                className="flex-1 rounded-lg px-4 data-[state=active]:text-slate-900 sm:flex-none"
              >
                Replays ({passes.length})
              </TabsTrigger>
            </TabsList>

            <TabsContent
              value="futurs"
              className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]"
            >
              <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
                  <CalendarDays className="h-5 w-5" />
                </div>
                <h2 className="font-bold text-slate-950">Prochains rendez-vous</h2>
              </div>
              {!webinaires ? (
                <div className="space-y-4 p-5 sm:p-6">
                  {[0, 1, 2].map((item) => (
                    <Skeleton key={item} className="h-36 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <WebinaireList webinaires={futurs} disableLiveAfterFirst />
              )}
            </TabsContent>

            <TabsContent
              value="passes"
              className="mt-4 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]"
            >
              <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
                  <History className="h-5 w-5" />
                </div>
                <h2 className="font-bold text-slate-950">Rencontres passées</h2>
              </div>
              {!webinaires ? (
                <div className="space-y-4 p-5 sm:p-6">
                  {[0, 1, 2].map((item) => (
                    <Skeleton key={item} className="h-36 w-full rounded-xl" />
                  ))}
                </div>
              ) : (
                <WebinaireList webinaires={passes} />
              )}
            </TabsContent>
          </Tabs>
        </>
      ) : !allowedError && allowed === false ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-[0_14px_40px_-34px_rgba(15,23,42,0.5)]">
          <div className="flex flex-col items-center px-6 py-12 text-center sm:py-16">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-[#f1e8c9] text-[#755c14]">
              <LockKeyhole className="h-7 w-7" />
            </div>
            <h2 className="mt-5 text-xl font-bold text-slate-950">Accès réservé aux abonnés</h2>
            <p className="mt-2 max-w-xl text-sm leading-6 text-slate-600">
              Un abonnement actif avec un pack Or, Argent ou Bronze est nécessaire pour accéder aux directs et aux
              replays.
            </p>
          </div>
        </section>
      ) : !allowedError && !webinairesError ? (
        <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
          <div className="flex items-center gap-3 border-b border-slate-100 bg-[#fbfaf6] px-5 py-4 sm:px-6">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-[#f1e8c9] text-[#755c14]">
              <Video className="h-5 w-5" />
            </div>
            <Skeleton className="h-5 w-44" />
          </div>
          <div className="space-y-4 p-5 sm:p-6">
            {[0, 1, 2].map((item) => (
              <Skeleton key={item} className="h-36 w-full rounded-xl" />
            ))}
          </div>
        </section>
      ) : null}
    </div>
  );
}
